import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/lib/seo";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingSections2";
import { List, ChevronDown, ArrowUp } from "lucide-react";

const SECTIONS = [
  { id: "s1", t: "1. Business Use", body: (
    <>
      <p>Fieldlink is primarily a business-to-business platform designed for organisations operating in industries including civil construction, earthworks, drainage, infrastructure, transport, plant, construction services and related industries.</p>
      <p>If you acquire the Services in trade for business purposes, you agree that, to the maximum extent permitted by law, the Consumer Guarantees Act 1993 does not apply to the Services and that it is fair and reasonable for the parties to be bound by this provision.</p>
      <p>Nothing in these Terms excludes or restricts any right or remedy that cannot lawfully be excluded or restricted.</p>
    </>
  ) },
  { id: "s2", t: "2. Fieldlink Services", body: (
    <>
      <p>Fieldlink may provide functionality relating to matters including:</p>
      <ul>
        <li>project and job management;</li>
        <li>estimating and takeoffs;</li>
        <li>quotes and variations;</li>
        <li>timesheets;</li>
        <li>payroll-related records;</li>
        <li>crew planning;</li>
        <li>workforce management;</li>
        <li>health and safety;</li>
        <li>toolbox talks;</li>
        <li>pre-starts;</li>
        <li>inspections;</li>
        <li>QA and project documentation;</li>
        <li>plant and fleet management;</li>
        <li>GPS and location functionality;</li>
        <li>dispatch;</li>
        <li>supply-yard management;</li>
        <li>weighbridge records;</li>
        <li>material movements;</li>
        <li>dockets;</li>
        <li>stockpiles and processing;</li>
        <li>invoicing;</li>
        <li>job costing;</li>
        <li>financial reporting;</li>
        <li>customer and supplier management;</li>
        <li>document management;</li>
        <li>communications;</li>
        <li>artificial intelligence features;</li>
        <li>third-party integrations; and</li>
        <li>other functionality introduced by Fieldlink.</li>
      </ul>
      <p>The exact Services available depend on the Customer&apos;s subscription, configuration and enabled modules.</p>
    </>
  ) },
  { id: "s3", t: "3. Accounts", body: (
    <>
      <p>The Customer is responsible for all accounts created under its organisation.</p>
      <p>The Customer must ensure that:</p>
      <ul>
        <li>account information is accurate;</li>
        <li>users are authorised to use the Services;</li>
        <li>appropriate roles and permissions are assigned;</li>
        <li>former workers and other unauthorised persons have their access removed promptly;</li>
        <li>login credentials are protected;</li>
        <li>accounts are not improperly shared;</li>
        <li>reasonable security practices are followed.</li>
      </ul>
      <p>The Customer is responsible for activity performed using its accounts except to the extent that the activity results directly from Fieldlink&apos;s breach of these Terms or applicable law.</p>
      <p>The Customer must notify Fieldlink promptly if it becomes aware of suspected unauthorised access or compromise of an account.</p>
    </>
  ) },
  { id: "s4", t: "4. Customer Data", body: (
    <>
      <p>&quot;Customer Data&quot; means information, documents, records, photographs, files, communications and other content submitted, uploaded, imported, collected, generated or stored by or for the Customer through the Services.</p>
      <p>As between Fieldlink and the Customer, the Customer retains its rights in Customer Data.</p>
      <p>The Customer grants Fieldlink a non-exclusive right to host, copy, transmit, process, display and otherwise use Customer Data only to the extent reasonably necessary to:</p>
      <ul>
        <li>provide the Services;</li>
        <li>maintain and secure the Services;</li>
        <li>provide support;</li>
        <li>operate Customer-enabled functionality;</li>
        <li>comply with law;</li>
        <li>investigate misuse or security incidents; and</li>
        <li>otherwise perform Fieldlink&apos;s obligations under the agreement.</li>
      </ul>
      <p>Fieldlink does not acquire ownership of Customer Data merely because it is stored within Fieldlink.</p>
    </>
  ) },
  { id: "s5", t: "5. Customer Responsibility for Data", body: (
    <>
      <p>The Customer controls the information that its users submit to Fieldlink.</p>
      <p>The Customer is responsible for ensuring that it has all permissions, authorities, notices, consents or other lawful grounds required to collect, upload, use and disclose Customer Data through the Services.</p>
      <p>This includes information relating to employees, workers, contractors, customers, suppliers, drivers, site visitors and other individuals.</p>
      <p>The Customer must not knowingly use Fieldlink to store or process information unlawfully.</p>
    </>
  ) },
  { id: "s6", t: "6. Privacy", body: (
    <>
      <p>Fieldlink will handle personal information in accordance with applicable New Zealand privacy law and the <Link to="/privacy" className="tos-link">Fieldlink Privacy Policy</Link>.</p>
      <p>Where Fieldlink processes personal information on behalf of a Customer, each party remains responsible for its own obligations under applicable privacy law.</p>
      <p>Customers must ensure their use of Fieldlink complies with their own privacy obligations, including providing any notices or obtaining any authorisations required for information they collect through Fieldlink.</p>
      <p>Nothing in these Terms excludes or modifies obligations imposed by the Privacy Act 2020 that cannot lawfully be excluded or modified.</p>
    </>
  ) },
  { id: "s7", t: "7. Information Security", body: (
    <>
      <p>Fieldlink will use reasonable technical and organisational safeguards designed to protect information held through the Services against unauthorised access, disclosure, alteration, loss, misuse and destruction.</p>
      <p>Depending on the Services and infrastructure involved, these safeguards may include measures such as:</p>
      <ul>
        <li>authentication;</li>
        <li>access controls;</li>
        <li>role-based permissions;</li>
        <li>encryption;</li>
        <li>secure communications;</li>
        <li>logging;</li>
        <li>monitoring;</li>
        <li>backups;</li>
        <li>infrastructure controls;</li>
        <li>audit records;</li>
        <li>vulnerability management; and</li>
        <li>incident response procedures.</li>
      </ul>
      <p>However, no internet-connected service, database, network, software platform, cloud provider, transmission method or electronic storage system can be guaranteed to be completely secure or continuously available.</p>
      <p>Fieldlink does not warrant that the Services will be immune from all cyberattacks, credential compromise, malicious activity, vulnerabilities, malware, infrastructure failures or other security incidents.</p>
      <p>This provision does not reduce Fieldlink&apos;s obligations under applicable law.</p>
    </>
  ) },
  { id: "s8", t: "8. Security Incidents and Data Breaches", body: (
    <>
      <p>A security or privacy incident may include unauthorised access, disclosure, alteration, loss, destruction or unavailability of information.</p>
      <p>If Fieldlink becomes aware of a suspected security incident affecting Customer Data, Fieldlink may:</p>
      <ul>
        <li>investigate;</li>
        <li>contain the incident;</li>
        <li>take remediation measures;</li>
        <li>preserve relevant evidence and logs;</li>
        <li>engage security specialists;</li>
        <li>suspend compromised accounts or functionality;</li>
        <li>require credentials to be reset;</li>
        <li>communicate with affected Customers;</li>
        <li>cooperate with relevant authorities; and</li>
        <li>take other reasonable measures to protect the Services and affected persons.</li>
      </ul>
      <p>Where an incident constitutes a notifiable privacy breach, Fieldlink will comply with applicable notification requirements.</p>
      <p>The Customer must reasonably cooperate with Fieldlink where an incident involves the Customer&apos;s accounts, users, devices, integrations or systems.</p>
    </>
  ) },
  { id: "s9", t: "9. Customer Security Responsibilities", body: (
    <>
      <p>Security is a shared responsibility.</p>
      <p>The Customer is responsible for security matters within its control, including:</p>
      <ul>
        <li>passwords;</li>
        <li>account access;</li>
        <li>user permissions;</li>
        <li>employee access;</li>
        <li>device security;</li>
        <li>email security;</li>
        <li>network security;</li>
        <li>exported data;</li>
        <li>downloaded documents;</li>
        <li>third-party integrations selected by the Customer; and</li>
        <li>protecting credentials and authentication methods.</li>
      </ul>
      <p>To the maximum extent permitted by law, Fieldlink will not be liable for a security incident to the extent it was caused by:</p>
      <ul>
        <li>compromised Customer credentials;</li>
        <li>passwords shared by Customer personnel;</li>
        <li>phishing of a Customer user;</li>
        <li>malware on Customer-controlled devices;</li>
        <li>failure by the Customer to remove an unauthorised user;</li>
        <li>incorrect permissions configured by the Customer;</li>
        <li>Customer disclosure of information;</li>
        <li>misuse by an authorised Customer user; or</li>
        <li>Customer-controlled systems or integrations,</li>
      </ul>
      <p>except to the extent Fieldlink caused or materially contributed to the relevant loss through its own breach of these Terms or applicable law.</p>
    </>
  ) },
  { id: "s10", t: "10. Data Backups and Data Loss", body: (
    <>
      <p>Fieldlink may maintain backups and disaster-recovery mechanisms as part of operating the Services.</p>
      <p>Backups are intended primarily for service continuity and disaster recovery and are not necessarily individual Customer archives.</p>
      <p>Unless Fieldlink expressly agrees otherwise in writing, Fieldlink does not guarantee that every item of Customer Data can be recovered following deletion, corruption, system failure or another incident.</p>
      <p>Customers should maintain copies of business-critical information where reasonable and appropriate for their operations.</p>
    </>
  ) },
  { id: "s11", t: "11. Service Availability", body: (
    <>
      <p>Fieldlink aims to provide reliable Services but does not guarantee uninterrupted or error-free availability unless a separate written service-level agreement expressly provides otherwise.</p>
      <p>The Services may occasionally be unavailable because of:</p>
      <ul>
        <li>maintenance;</li>
        <li>updates;</li>
        <li>software defects;</li>
        <li>telecommunications failures;</li>
        <li>infrastructure failures;</li>
        <li>cloud provider outages;</li>
        <li>third-party service outages;</li>
        <li>cyber incidents;</li>
        <li>emergency maintenance;</li>
        <li>events beyond Fieldlink&apos;s reasonable control.</li>
      </ul>
      <p>Fieldlink may perform maintenance and updates where reasonably necessary to maintain security, reliability or functionality.</p>
    </>
  ) },
  { id: "s12", t: "12. Changes to the Services", body: (
    <>
      <p>Fieldlink is continually developed.</p>
      <p>We may add, change, improve, replace or discontinue features.</p>
      <p>We will take reasonable steps to avoid materially reducing core paid functionality during an active subscription without appropriate notice where reasonably practicable.</p>
      <p>We may make immediate changes where reasonably necessary for security, legal compliance, system integrity or prevention of harm.</p>
    </>
  ) },
  { id: "s13", t: "13. Third-Party Services", body: (
    <>
      <p>Fieldlink may integrate with third-party products and services, including accounting, payment, mapping, communications, cloud, artificial intelligence and other platforms.</p>
      <p>Third-party services are not controlled by Fieldlink merely because they integrate with Fieldlink.</p>
      <p>Fieldlink is not responsible for the availability, performance, security, accuracy or independent conduct of a third-party service except to the extent Fieldlink is legally responsible for its selection or use of that provider.</p>
      <p>Use of a third-party service may also be subject to that provider&apos;s terms and privacy policy.</p>
    </>
  ) },
  { id: "s14", t: "14. Integrations", body: (
    <>
      <p>Where the Customer authorises an integration, the Customer authorises Fieldlink to exchange information with the relevant provider to the extent reasonably required to operate that integration.</p>
      <p>The Customer is responsible for verifying information transferred between systems where that information affects financial, payroll, operational, safety or other important decisions.</p>
      <p>Fieldlink does not warrant that a third-party API will remain available or unchanged.</p>
      <p>If a third-party provider changes or discontinues its API or service, Fieldlink may modify, suspend or discontinue the affected integration.</p>
    </>
  ) },
  { id: "s15", t: "15. Accounting and Financial Information", body: (
    <>
      <p>Fieldlink may provide invoicing, costing, estimating, quoting, reporting and accounting-related functionality.</p>
      <p>Fieldlink is not an accounting firm, financial adviser, tax adviser or auditor.</p>
      <p>Calculations and reports depend on the information and configuration supplied by Customers and connected systems.</p>
      <p>The Customer remains responsible for reviewing financial information before relying on it for accounting, taxation, payroll, contractual or commercial purposes.</p>
    </>
  ) },
  { id: "s16", t: "16. Estimating, Takeoffs and Quantities", body: (
    <>
      <p>Fieldlink may assist with:</p>
      <ul>
        <li>estimates;</li>
        <li>rates;</li>
        <li>quantities;</li>
        <li>takeoffs;</li>
        <li>measurements;</li>
        <li>job costing;</li>
        <li>material calculations; and</li>
        <li>tender preparation.</li>
      </ul>
      <p>These tools assist professional decision-making but do not replace verification by appropriately competent personnel.</p>
      <p>Customers are responsible for checking quantities, dimensions, rates, drawings, specifications, assumptions and scope before submitting tenders, ordering materials or carrying out work.</p>
      <p>Fieldlink does not guarantee that an estimate, takeoff, rate recommendation or calculated quantity is complete or suitable for a particular project.</p>
    </>
  ) },
  { id: "s17", t: "17. Artificial Intelligence", body: (
    <>
      <p>Fieldlink may provide artificial intelligence, machine-learning or automated-assistance functionality, including features such as Kiri or other Fieldlink AI functionality.</p>
      <p>AI output may be incomplete, inaccurate or inappropriate.</p>
      <p>AI-generated information is provided as assistance and must not be treated as automatically verified fact.</p>
      <p>The Customer is responsible for reviewing AI output before relying on it.</p>
      <p>Fieldlink AI does not replace:</p>
      <ul>
        <li>engineering judgement;</li>
        <li>professional advice;</li>
        <li>site verification;</li>
        <li>safety assessments;</li>
        <li>legal advice;</li>
        <li>accounting advice;</li>
        <li>contractual review; or</li>
        <li>competent human supervision.</li>
      </ul>
      <p>Fieldlink may prevent AI functionality from automatically performing certain high-impact actions without user confirmation.</p>
    </>
  ) },
  { id: "s18", t: "18. Health and Safety", body: (
    <>
      <p>Fieldlink may provide tools to assist Customers with health and safety administration and records.</p>
      <p>These may include:</p>
      <ul>
        <li>toolbox talks;</li>
        <li>hazard registers;</li>
        <li>pre-starts;</li>
        <li>inspections;</li>
        <li>competency records;</li>
        <li>incident records;</li>
        <li>safety documentation;</li>
        <li>sign-ons; and</li>
        <li>forms.</li>
      </ul>
      <p>Fieldlink is a software tool.</p>
      <p>It does not assume the Customer&apos;s duties under the Health and Safety at Work Act 2015 or other applicable health and safety laws.</p>
      <p>Use of a Fieldlink template, form, checklist or AI-generated suggestion does not establish that a worksite, system of work or activity is safe or legally compliant.</p>
      <p>The Customer remains responsible for identifying hazards and risks and ensuring work is planned, supervised and carried out appropriately.</p>
    </>
  ) },
  { id: "s19", t: "19. Engineering and Technical Information", body: (
    <>
      <p>Fieldlink does not provide professional engineering services merely by providing calculations, measurements, drawings, AI assistance or other software functionality.</p>
      <p>Engineering, surveying or other technical information produced or stored through Fieldlink must be independently verified where professional verification is required.</p>
      <p>The Customer remains responsible for determining whether professional engineering, surveying or other specialist advice is required.</p>
    </>
  ) },
  { id: "s20", t: "20. GPS and Location Information", body: (
    <>
      <p>Where location functionality is enabled, Fieldlink may display locations derived from GPS devices, phones, vehicles or third-party services.</p>
      <p>Location information may be delayed, unavailable or inaccurate because of matters including:</p>
      <ul>
        <li>GPS conditions;</li>
        <li>hardware;</li>
        <li>connectivity;</li>
        <li>device configuration;</li>
        <li>battery status;</li>
        <li>third-party systems; or</li>
        <li>user permissions.</li>
      </ul>
      <p>Fieldlink must not be used as the sole means of locating a person in an emergency or for safety-critical navigation.</p>
    </>
  ) },
  { id: "s21", t: "21. Supply Yard and Weighbridge Functionality", body: (
    <>
      <p>Fieldlink may record supply-yard visits, loads, materials, stock, weighbridge measurements, dockets and related transactions.</p>
      <p>Fieldlink records information supplied by users, connected equipment and configured systems.</p>
      <p>Unless Fieldlink expressly provides a certified measurement service, Fieldlink does not itself certify the accuracy or legal status of physical scales, weighbridges, loader scales or measurement devices.</p>
      <p>Customers remain responsible for ensuring measurement equipment and commercial transactions comply with applicable requirements.</p>
      <p>Estimated quantities must not be represented as measured quantities solely because they are recorded within Fieldlink.</p>
    </>
  ) },
  { id: "s22", t: "22. Customer Content and Documents", body: (
    <>
      <p>The Customer is responsible for Customer Data uploaded to the Services.</p>
      <p>The Customer warrants that its use of Customer Data through Fieldlink will not knowingly:</p>
      <ul>
        <li>violate applicable law;</li>
        <li>infringe another person&apos;s intellectual property rights;</li>
        <li>unlawfully disclose confidential information;</li>
        <li>contain malicious software; or</li>
        <li>materially interfere with the Services.</li>
      </ul>
      <p>Fieldlink may restrict or remove content where reasonably necessary to comply with law or protect the Services.</p>
    </>
  ) },
  { id: "s23", t: "23. Acceptable Use", body: (
    <>
      <p>Customers and users must not:</p>
      <ul>
        <li>attempt to gain unauthorised access to Fieldlink;</li>
        <li>bypass access controls;</li>
        <li>interfere with the Services;</li>
        <li>introduce malware;</li>
        <li>probe or attack Fieldlink infrastructure without written authorisation;</li>
        <li>use the Services unlawfully;</li>
        <li>impersonate another person;</li>
        <li>knowingly upload unlawful material;</li>
        <li>use Fieldlink to facilitate fraud;</li>
        <li>scrape or extract the Services in an unauthorised manner;</li>
        <li>reverse engineer the Services except where the law expressly permits it;</li>
        <li>provide unauthorised third parties with access to Fieldlink; or</li>
        <li>use the Services in a manner reasonably likely to compromise security or availability.</li>
      </ul>
    </>
  ) },
  { id: "s24", t: "24. Intellectual Property", body: (
    <>
      <p>Fieldlink and its licensors retain all intellectual property rights in the Services, including:</p>
      <ul>
        <li>software;</li>
        <li>source code;</li>
        <li>application design;</li>
        <li>databases and database structures;</li>
        <li>workflows;</li>
        <li>interfaces;</li>
        <li>graphics;</li>
        <li>branding;</li>
        <li>documentation;</li>
        <li>templates;</li>
        <li>underlying technology; and</li>
        <li>improvements to the Services.</li>
      </ul>
      <p>Except for the limited right to use the Services under these Terms, no intellectual property rights are transferred to the Customer.</p>
      <p>Customer Data remains subject to section 4.</p>
    </>
  ) },
  { id: "s25", t: "25. Feedback", body: (
    <>
      <p>If the Customer voluntarily provides ideas, suggestions or feedback concerning Fieldlink, Fieldlink may use that feedback to develop or improve the Services without restriction or payment, provided doing so does not give Fieldlink ownership of the Customer&apos;s confidential information or Customer Data.</p>
    </>
  ) },
  { id: "s26", t: "26. Subscriptions", body: (
    <>
      <p>Access to paid Services requires payment of applicable subscription fees.</p>
      <p>Subscription plans, included functionality, limits and pricing are those presented at the time of purchase or otherwise agreed in writing.</p>
      <p>Fees are stated exclusive of GST unless expressly stated otherwise.</p>
      <p>Fieldlink may change subscription pricing by giving reasonable notice before the change applies to the Customer&apos;s next applicable renewal period.</p>
    </>
  ) },
  { id: "s27", t: "27. Payment", body: (
    <>
      <p>The Customer must pay all valid charges when due.</p>
      <p>Where recurring billing is enabled, the Customer authorises the applicable payment provider to charge the nominated payment method.</p>
      <p>If payment becomes overdue, Fieldlink may, after reasonable notice where appropriate:</p>
      <ul>
        <li>restrict paid functionality;</li>
        <li>suspend the account; or</li>
        <li>terminate the subscription.</li>
      </ul>
      <p>Suspension does not extinguish amounts already owing.</p>
    </>
  ) },
  { id: "s28", t: "28. Free Trials", body: (
    <>
      <p>Fieldlink may provide free trials.</p>
      <p>Trial functionality may be limited and may change.</p>
      <p>Unless otherwise stated, trial access may be terminated or converted to a paid subscription when the trial period ends.</p>
      <p>Customers should export any information they require before allowing a trial account to expire.</p>
    </>
  ) },
  { id: "s29", t: "29. Suspension", body: (
    <>
      <p>Fieldlink may immediately suspend access where reasonably necessary to:</p>
      <ul>
        <li>address a security threat;</li>
        <li>prevent unlawful use;</li>
        <li>protect other customers;</li>
        <li>prevent material harm to the Services;</li>
        <li>respond to a legal requirement; or</li>
        <li>contain compromised credentials.</li>
      </ul>
      <p>Where reasonably practicable, Fieldlink will notify the Customer and restore access when the relevant issue has been resolved.</p>
    </>
  ) },
  { id: "s30", t: "30. Termination", body: (
    <>
      <p>Either party may terminate the agreement in accordance with the applicable subscription or agreed commercial terms.</p>
      <p>Fieldlink may terminate or suspend the agreement for material breach if the breach is not remedied within a reasonable period after notice, where the breach is capable of remedy.</p>
      <p>Fieldlink may terminate immediately where continued access would be unlawful or creates a material security risk.</p>
    </>
  ) },
  { id: "s31", t: "31. Data Following Termination", body: (
    <>
      <p>Following termination or expiry, the Customer should export any Customer Data it wishes to retain.</p>
      <p>Fieldlink may provide a reasonable period for export where technically and legally practicable.</p>
      <p>After the applicable retention period, Fieldlink may delete Customer Data, except where retention is required or permitted for:</p>
      <ul>
        <li>legal obligations;</li>
        <li>dispute resolution;</li>
        <li>fraud prevention;</li>
        <li>security;</li>
        <li>audit requirements; or</li>
        <li>backup and disaster recovery.</li>
      </ul>
      <p>Deletion from active systems may not result in immediate deletion from backups.</p>
    </>
  ) },
  { id: "s32", t: "32. Confidentiality", body: (
    <>
      <p>Each party must take reasonable steps to protect confidential information received from the other party.</p>
      <p>Confidential information does not include information that:</p>
      <ul>
        <li>is publicly available other than through breach;</li>
        <li>was already lawfully known;</li>
        <li>is independently developed without use of the confidential information; or</li>
        <li>is lawfully obtained from another source without confidentiality restrictions.</li>
      </ul>
      <p>A party may disclose confidential information where required by law.</p>
    </>
  ) },
  { id: "s33", t: "33. Warranties", body: (
    <>
      <p>Each party warrants that it has authority to enter into this agreement.</p>
      <p>Except for express warranties contained in these Terms and warranties that cannot lawfully be excluded, the Services are provided on an &quot;as available&quot; basis.</p>
      <p>To the maximum extent permitted by law, Fieldlink does not warrant that:</p>
      <ul>
        <li>the Services will always be uninterrupted;</li>
        <li>every software defect will be corrected immediately;</li>
        <li>Customer Data can never be lost or corrupted;</li>
        <li>cyber incidents will never occur;</li>
        <li>third-party services will always be available;</li>
        <li>every calculation will be error-free;</li>
        <li>AI output will always be accurate; or</li>
        <li>the Services alone will satisfy every regulatory or operational requirement applying to the Customer.</li>
      </ul>
    </>
  ) },
  { id: "s34", t: "34. Customer Responsibility for Decisions", body: (
    <>
      <p>Fieldlink assists Customers with operating their businesses.</p>
      <p>The Customer remains responsible for decisions made using information provided through Fieldlink.</p>
      <p>Before taking material financial, contractual, engineering, safety, employment or operational action, the Customer must exercise appropriate professional judgement and verification.</p>
    </>
  ) },
  { id: "s35", t: "35. Exclusion of Certain Loss", body: (
    <>
      <p>To the maximum extent permitted by law, neither party will be liable to the other for any indirect, consequential, incidental, special or exemplary loss arising out of or relating to the Services or these Terms.</p>
      <p>To the maximum extent permitted by law, Fieldlink will not be liable for loss of:</p>
      <ul>
        <li>profit;</li>
        <li>revenue;</li>
        <li>anticipated savings;</li>
        <li>business opportunity;</li>
        <li>contracts;</li>
        <li>goodwill; or</li>
        <li>business interruption,</li>
      </ul>
      <p>to the extent such loss is indirect or consequential.</p>
      <p>This section does not apply where liability cannot lawfully be excluded.</p>
    </>
  ) },
  { id: "s36", t: "36. Data and Cyber Loss", body: (
    <>
      <p>Subject to applicable law and the liability cap below, Fieldlink is not automatically liable merely because Customer Data becomes unavailable, corrupted, disclosed or accessed without authorisation.</p>
      <p>Liability will depend on the circumstances and the parties&apos; respective legal and contractual responsibilities.</p>
      <p>To the maximum extent permitted by law, Fieldlink will not be liable for loss caused by circumstances outside Fieldlink&apos;s reasonable control, including:</p>
      <ul>
        <li>Customer credential compromise;</li>
        <li>Customer-controlled devices or networks;</li>
        <li>Customer-selected third-party integrations;</li>
        <li>malicious acts by Customer personnel;</li>
        <li>telecommunications failures; or</li>
        <li>failures of third parties that Fieldlink could not reasonably prevent,</li>
      </ul>
      <p>except to the extent Fieldlink caused or materially contributed to the relevant loss through its own breach of these Terms or applicable law.</p>
      <p>Nothing in this section limits Fieldlink&apos;s obligations under the Privacy Act 2020 or any liability that cannot lawfully be excluded.</p>
    </>
  ) },
  { id: "s37", t: "37. Limitation of Liability", body: (
    <>
      <p>To the maximum extent permitted by law, Fieldlink&apos;s total aggregate liability arising out of or relating to the Services, these Terms or the relationship between Fieldlink and the Customer will not exceed the total subscription fees actually paid by the Customer to Fieldlink during the 12 months immediately preceding the event giving rise to the claim.</p>
      <p>If the Customer has used the Services for less than 12 months, the cap will be the subscription fees actually paid during that period.</p>
      <p>For a free account or free trial where no subscription fees have been paid, Fieldlink&apos;s aggregate liability will not exceed NZ$100, except where such limitation would be unlawful.</p>
      <p>The liability cap applies collectively to all claims arising from the same or related circumstances and is not multiplied by the number of claims, users or affected records.</p>
      <p>The limitations in these Terms apply regardless of whether a claim is based in contract, tort including negligence, statute or another legal basis.</p>
      <p>The limitation does not apply to the extent liability cannot lawfully be excluded or limited.</p>
    </>
  ) },
  { id: "s38", t: "38. Customer Indemnity", body: (
    <>
      <p>To the extent permitted by law, the Customer indemnifies Fieldlink against third-party claims, losses, liabilities and reasonable costs arising directly from:</p>
      <ul>
        <li>unlawful Customer Data supplied by the Customer;</li>
        <li>the Customer&apos;s material breach of these Terms;</li>
        <li>the Customer&apos;s unlawful use of the Services;</li>
        <li>infringement of third-party rights by Customer Data; or</li>
        <li>the Customer&apos;s failure to obtain permissions it was legally required to obtain,</li>
      </ul>
      <p>except to the extent the claim was caused or materially contributed to by Fieldlink&apos;s breach of these Terms, negligence or unlawful conduct.</p>
    </>
  ) },
  { id: "s39", t: "39. Insurance", body: (
    <>
      <p>Each party is responsible for maintaining insurance appropriate to its own business, risks and legal obligations.</p>
      <p>Use of Fieldlink does not replace the Customer&apos;s responsibility to consider appropriate cyber, professional, public liability, statutory liability or other insurance.</p>
    </>
  ) },
  { id: "s40", t: "40. Force Majeure", body: (
    <>
      <p>Neither party is liable for delay or failure to perform an obligation, other than an obligation to pay amounts already due, where the delay or failure results from circumstances beyond that party&apos;s reasonable control.</p>
      <p>Such circumstances may include natural disasters, fire, flood, earthquake, epidemic, war, civil disturbance, government action, widespread telecommunications or electricity failure, or major infrastructure failure.</p>
      <p>The affected party must take reasonable steps to minimise the impact where practicable.</p>
    </>
  ) },
  { id: "s41", t: "41. Notices", body: (
    <>
      <p>Fieldlink may provide contractual or service notices through:</p>
      <ul>
        <li>email;</li>
        <li>the Services;</li>
        <li>account notifications; or</li>
        <li>another contact method supplied by the Customer.</li>
      </ul>
      <p>Customers are responsible for keeping their contact information current.</p>
    </>
  ) },
  { id: "s42", t: "42. Changes to These Terms", body: (
    <>
      <p>Fieldlink may amend these Terms from time to time.</p>
      <p>Where a change materially affects Customer rights or obligations, Fieldlink will provide reasonable notice before the change takes effect unless an immediate change is reasonably required by law, security requirements or circumstances outside Fieldlink&apos;s control.</p>
      <p>Continued use after the effective date constitutes acceptance to the extent permitted by law.</p>
      <p>If a Customer does not agree to a material change, it may exercise any termination rights available under its subscription agreement.</p>
    </>
  ) },
  { id: "s43", t: "43. Assignment", body: (
    <>
      <p>The Customer may not assign its agreement with Fieldlink without Fieldlink&apos;s prior written consent, which will not be unreasonably withheld.</p>
      <p>Fieldlink may assign the agreement as part of a bona fide merger, restructuring, financing, sale of business or transfer of substantially all relevant assets, provided the assignee assumes Fieldlink&apos;s material obligations under the agreement.</p>
    </>
  ) },
  { id: "s44", t: "44. No Partnership", body: (
    <>
      <p>Nothing in these Terms creates a partnership, joint venture, employment, fiduciary or agency relationship between Fieldlink and the Customer.</p>
    </>
  ) },
  { id: "s45", t: "45. Waiver", body: (
    <>
      <p>Failure to enforce a provision of these Terms does not waive that provision or any other right.</p>
    </>
  ) },
  { id: "s46", t: "46. Severability", body: (
    <>
      <p>If a provision of these Terms is held to be unlawful or unenforceable, it will be modified to the minimum extent necessary to make it enforceable where possible.</p>
      <p>If it cannot be modified, it will be severed.</p>
      <p>The remaining Terms continue in effect.</p>
    </>
  ) },
  { id: "s47", t: "47. Entire Agreement", body: (
    <>
      <p>These Terms, together with:</p>
      <ul>
        <li>the Fieldlink Privacy Policy;</li>
        <li>the applicable subscription or order;</li>
        <li>any agreed Data Processing Terms;</li>
        <li>any applicable Service Level Agreement; and</li>
        <li>other terms expressly incorporated by reference,</li>
      </ul>
      <p>constitute the agreement concerning the Services.</p>
      <p>If there is a conflict, any specifically negotiated written agreement or order form will take priority to the extent of the conflict.</p>
    </>
  ) },
  { id: "s48", t: "48. Disputes", body: (
    <>
      <p>Before commencing court proceedings, each party will use reasonable efforts to resolve a dispute through good-faith discussions between authorised representatives.</p>
      <p>Either party may seek urgent interim or injunctive relief where reasonably necessary.</p>
      <p>Nothing in this section prevents either party from exercising rights that cannot lawfully be restricted.</p>
    </>
  ) },
  { id: "s49", t: "49. New Zealand Law", body: (
    <>
      <p>These Terms and the relationship between Fieldlink and the Customer are governed by the laws of New Zealand.</p>
      <p>The parties submit to the non-exclusive jurisdiction of the New Zealand courts.</p>
    </>
  ) },
  { id: "s50", t: "50. Contact", body: (
    <>
      <p>Fieldlink</p>
      <p>Legal entity:<br />Fieldlink Aotearoa Ltd</p>
      <p>General enquiries:<br /><a href="mailto:support@fieldlink.co.nz" className="tos-link">support@fieldlink.co.nz</a></p>
      <p>Legal enquiries:<br /><a href="mailto:legal@fieldlink.co.nz" className="tos-link">legal@fieldlink.co.nz</a></p>
      <p>Privacy enquiries:<br /><a href="mailto:privacy@fieldlink.co.nz" className="tos-link">privacy@fieldlink.co.nz</a></p>
      <p>Security reports:<br /><a href="mailto:security@fieldlink.co.nz" className="tos-link">security@fieldlink.co.nz</a></p>
      <p>Website:<br /><a href="https://www.fieldlink.co.nz" className="tos-link" rel="noreferrer" target="_blank">www.fieldlink.co.nz</a></p>
    </>
  ) },
  { id: "s51", t: "51. Acceptance", body: (
    <>
      <p>By creating an account, purchasing a subscription, clicking an acceptance button, or otherwise using the Services after being presented with these Terms, the Customer confirms that:</p>
      <ol>
        <li>it has read and understood these Terms;</li>
        <li>it agrees to be bound by them;</li>
        <li>where acting for an organisation, it has authority to bind that organisation; and</li>
        <li>where the Services are acquired in trade and the requirements of section 1 are satisfied, it agrees to the stated contracting-out provision.</li>
      </ol>
    </>
  ) },
];

const LAST_UPDATED = "14 September 2026";

export default function Terms() {
  const [tocOpen, setTocOpen] = useState(false);
  return (
    <>
      <SEO
        title="Fieldlink Terms of Service | NZ Civil Construction Software"
        description="Terms governing the use of Fieldlink civil construction management software and related services."
        path="/terms"
      />
      <div className="terms-page min-h-screen bg-[#F4F4F1] text-[#161A20]" id="top">
        <div className="no-print"><MarketingHeader /></div>

        {/* Hero */}
        <header className="border-b border-[#161A20]/10 bg-white">
          <div className="max-w-[1050px] mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="text-[11px] font-mono font-bold uppercase tracking-[0.32em] text-[#FF5F15] mb-4">Fieldlink</div>
            <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#161A20]" data-testid="terms-page-title">
              Terms of Service
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#161A20]/60">Built for what&apos;s next.</p>
            <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-[#161A20]/50">Last updated · {LAST_UPDATED}</p>
          </div>
        </header>

        <div className="max-w-[1050px] mx-auto px-6 md:px-10 py-10 lg:py-14 lg:grid lg:grid-cols-[1fr_260px] lg:gap-12 lg:items-start">
          {/* Main content */}
          <main className="terms-card order-2 lg:order-1 min-w-0">
            {/* Mobile TOC */}
            <div className="lg:hidden no-print mb-8 rounded-lg border border-[#161A20]/12 bg-white overflow-hidden">
              <button
                onClick={() => setTocOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-[12px] font-bold uppercase tracking-[0.22em] text-[#161A20]"
                data-testid="terms-toc-toggle"
              >
                <span className="flex items-center gap-2"><List className="w-4 h-4 text-[#FF5F15]" /> Contents</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${tocOpen ? "rotate-180" : ""}`} />
              </button>
              {tocOpen && (
                <nav className="px-4 pb-4 max-h-[50vh] overflow-y-auto border-t border-[#161A20]/10 pt-3">
                  <ul className="space-y-1.5">
                    {SECTIONS.map((s) => (
                      <li key={s.id}>
                        <a href={`#${s.id}`} onClick={() => setTocOpen(false)} className="block text-sm text-[#161A20]/70 hover:text-[#FF5F15]">{s.t}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </div>

            <article className="terms-prose">
              <p className="lead">These Terms of Service (&quot;Terms&quot;) govern access to and use of Fieldlink&apos;s software, websites, applications, mobile applications, APIs, integrations and related services (collectively, the &quot;Services&quot;).</p>
              <p>These Terms form a legally binding agreement between Fieldlink Aotearoa Ltd (&quot;Fieldlink&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;), and the person or organisation agreeing to these Terms (&quot;Customer&quot;, &quot;you&quot; or &quot;your&quot;).</p>
              <p>By creating an account, purchasing a subscription, accessing or using the Services, or authorising another person to use the Services on your behalf, you agree to these Terms.</p>
              <p>If you accept these Terms on behalf of an organisation, you confirm that you have authority to bind that organisation.</p>

              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} className="terms-section scroll-mt-28">
                  <h2>{s.t}</h2>
                  {s.body}
                </section>
              ))}
            </article>

            <div className="no-print mt-14 pt-6 border-t border-[#161A20]/10 flex flex-wrap items-center justify-between gap-3 text-xs text-[#161A20]/50">
              <span>© 2026 Fieldlink Aotearoa Ltd · 100% NZ owned</span>
              <a href="#top" className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wider hover:text-[#FF5F15]"><ArrowUp className="w-3.5 h-3.5" /> Back to top</a>
            </div>
          </main>

          {/* Desktop sticky TOC */}
          <aside className="no-print hidden lg:block order-1 lg:order-2">
            <nav className="sticky top-24" aria-label="Table of contents">
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#161A20]/45 mb-3 flex items-center gap-2"><List className="w-3.5 h-3.5 text-[#FF5F15]" /> Contents</div>
              <ul className="space-y-1.5 max-h-[calc(100vh-9rem)] overflow-y-auto pr-2 border-l-2 border-[#161A20]/10">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="block pl-3 -ml-[2px] border-l-2 border-transparent hover:border-[#FF5F15] text-[13px] leading-snug text-[#161A20]/65 hover:text-[#FF5F15] transition-colors">{s.t}</a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </div>

        <div className="no-print"><MarketingFooter /></div>
      </div>

      <style>{`
        .terms-prose { color: #2A2F37; }
        .terms-prose .lead { font-size: 1.05rem; line-height: 1.85; color: #161A20; font-weight: 500; }
        .terms-prose p { font-size: 0.95rem; line-height: 1.85; margin-bottom: 0.85rem; }
        .terms-prose ul, .terms-prose ol { margin: 0 0 1rem 0; padding-left: 1.35rem; }
        .terms-prose ul { list-style: disc; }
        .terms-prose ol { list-style: decimal; }
        .terms-prose li { font-size: 0.95rem; line-height: 1.7; margin-bottom: 0.3rem; }
        .terms-section { margin-top: 2.6rem; padding-top: 0.4rem; }
        .terms-section h2 {
          font-family: var(--font-display, inherit);
          font-weight: 800; text-transform: uppercase; letter-spacing: -0.01em;
          font-size: 1.15rem; color: #161A20; margin-bottom: 0.85rem;
          padding-bottom: 0.5rem; border-bottom: 2px solid rgba(255,95,21,0.25);
        }
        .tos-link { color: #C2440B; text-decoration: underline; text-underline-offset: 2px; }
        .tos-link:hover { color: #FF5F15; }
        @media print {
          .no-print { display: none !important; }
          .terms-page { background: #fff !important; color: #000 !important; }
          .terms-page header { border: none !important; }
          .terms-section { break-inside: avoid; }
          .terms-section h2 { border-color: #000 !important; }
          a[href^="http"]::after, a[href^="mailto"]::after { content: ""; }
        }
      `}</style>
    </>
  );
}
